import axios from 'axios'

/**
 * publicFilesApi.js
 *
 * API methods for public (unauthenticated) file access and file sharing.
 *
 * DOCS_API_URL  →  Spr2.Api.Documents (hosts public file + share endpoints)
 *   dev:   https://localhost:44325/
 *   test:  https://documents-test.responsegrid.com/
 *   prod:  https://documents.responsegrid.com/
 */

function docsBase () {
  return `${process.env.DOCS_API_URL}api`
}

export default {
  /**
   * Resolve a single-file public access token.
   * Called from PublicFileView.vue when a QR code URL is opened.
   * Returns PublicFileAccessResult on 200, 404 = not found, 410 = revoked.
   *
   * @param {string} companyId - from route param :companyId (encoded in QR code URL)
   * @param {string} token     - PublicAccessToken UUID from route param :token
   */
  getPublicFile (companyId, token) {
    return axios.get(`${docsBase()}/public/company/${companyId}/file/${token}`)
  },

  /**
   * Resolve a multi-file public share collection.
   * Called from PublicShareView.vue when a share URL is opened.
   * Returns GetPublicShareResponse (title, description, files[]) on 200, 404/410 on error.
   *
   * @param {string} companyId - from route param :companyId
   * @param {string} shareId   - from route param :shareId
   */
  getPublicShare (companyId, shareId) {
    return axios.get(`${docsBase()}/public/company/${companyId}/share/${shareId}`)
  },

  /**
   * Create a new multi-file public share for a resource (event, property, etc.).
   * Requires authentication — called by logged-in Response Grid users from EventManage.
   *
   * @param {string} companyId
   * @param {Object} payload   - CreatePublicFileShareRequest:
   *   { resourceId, resourceType, title?, description?, fileIds?, expiresDateUtc? }
   *   resourceType is the FileResourceType enum string, e.g. 'Event'
   */
  createPublicFileShare (companyId, payload) {
    return axios.post(`${docsBase()}/company/${companyId}/public-shares`, payload)
  },

  /**
   * Get the current video processing status + pre-signed URLs.
   * Called by the video status polling logic in Resources.vue.
   *
   * @param {string} companyId
   * @param {string} fileId
   */
  getVideoProcessingStatus (companyId, fileId) {
    return axios.get(`${docsBase()}/company/${companyId}/files/${fileId}/video-status`)
  },

  /**
   * Get all active (non-revoked) shares for a resource (e.g. an event).
   * Called by EventManage when the Documents tab loads.
   *
   * @param {string} companyId
   * @param {string} resourceId - the event / property / company ID
   */
  getSharesForResource (companyId, resourceId) {
    return axios.get(`${docsBase()}/company/${companyId}/public-shares`, { params: { resourceId } })
  },

  /**
   * Revoke a public share, permanently disabling the public link.
   *
   * @param {string} companyId
   * @param {string} shareId
   */
  revokeShare (companyId, shareId) {
    return axios.delete(`${docsBase()}/company/${companyId}/public-shares/${shareId}`)
  },

  /**
   * Fetch the flat file tree for an event (root-level resources).
   * Returns the same GetFileResourcesResponse shape that Resources.vue uses:
   * { folders: [...], documents: [...], images: [...], videos: [...] }
   *
   * Each folder has { id, name, childCount, ... } and each file has { id, name, folderId, ... }
   * Used by the file-selection step of the Share Files modal to build a pick-list.
   *
   * @param {string} companyId
   * @param {string} eventId
   */
  getEventResources (companyId, eventId) {
    return axios.get(`/api/company/${companyId}/events/${eventId}/resources`)
  },

  /**
   * Fetch the resource contents of a specific folder inside an event.
   * Returns the same GetFileResourcesResponse shape.
   *
   * @param {string} companyId
   * @param {string} eventId
   * @param {string} folderId
   */
  getEventFolderResources (companyId, eventId, folderId) {
    return axios.get(`/api/company/${companyId}/events/${eventId}/resources/folders/${folderId}`)
  },

  /**
   * Update the file selection on an existing share in-place (same URL, same expiry).
   * @param {string} companyId
   * @param {string} shareId
   * @param {string[]|undefined} fileIds - undefined or empty array = share all
   */
  updateShareFileIds (companyId, shareId, fileIds, expiresDateUtc, updateExpiry) {
    return axios.patch(`${docsBase()}/company/${companyId}/public-shares/${shareId}/file-ids`, {
      fileIds,
      expiresDateUtc,
      updateExpiry
    })
  }
}



// WEBPACK FOOTER //
// ./src/api/publicFilesApi.js